import Image from "next/image";
import Link from "next/link";
import { FC, useCallback, useEffect, useState } from "react";

import SubLayout from "@/containers/layout/LandingLayout/SubLayout";
import routes from "@/constants/routes";
import { getNeighbourhood } from "@/services/cms/api";

export type NeighbourhoodSlug =
  | "bermondsey"
  | "clapham"
  | "canning-town"
  | "london"
  | "croydon"
  | "southall"
  | "ealing"
  | "hanwell"
  | "harrow"
  | "hayes"
  | "tootingBroadway"
  | "wandsworth";

// Props
type NeighbourhoodProps = {
  neighbourhood: NeighbourhoodSlug;
};

export interface NeighbourhoodAttribute {
  heading: string;
  subheading: string;
  bullets: string[];
  paragraph: string;
}

const Neighbourhood: FC<NeighbourhoodProps> = ({ neighbourhood }) => {
  const [content, setContent] = useState<NeighbourhoodAttribute | null>(null);
  const getNeighbourhoodContent = useCallback(async () => {
    const response = await getNeighbourhood(neighbourhood);
    if (response.data && response.data.length > 0) {
      setContent({
        heading: response.data[0].attributes.heading,
        subheading: response.data[0].attributes.subHeading,
        bullets: response.data[0].attributes.bullets
          .split("\n")
          .map((bullet: string) => bullet.trim()),
        paragraph: response.data[0].attributes.paragraph,
      });
    } else {
      setContent(null);
    }
  }, [neighbourhood]);

  useEffect(() => {
    getNeighbourhoodContent();
  }, [neighbourhood, getNeighbourhoodContent]);

  if (!content) return null;

  return (
    <div className="w-full text-white pt-7 md:pt-0 xl:pt-28 bg-landing-blue -mt-20">
      <h2 className="font-bold mt-[2rem] md:mt-0 sm:pt-[7rem] xl:pt-[1rem] text-[2.625rem] xl:text-4xl leading-[49px] xl:leading-[61px] text-center lg:mb-10 bg ">
        {content.heading}
      </h2>
      <SubLayout>
        <div className="flex flex-col-reverse md:flex-row items-center justify-center mx-auto xl:max-w-6xl 3xl:max-w-screen-2xl md:pb-11 xl:pb-2 ">
          <div className="w-full xl:ml-0 md:w-1/2 md:pt-0 ">
            <div className="-right-10 xs:right-auto md:top-10 xl:top-auto h-72 xl:w-[34.5rem] xl:h-[28em] relative md:w-full md:h-[20.5rem] md:ml-auto">
              <Image
                src="/neighbourhoods/neighbourhood-image.svg"
                alt="home image"
                objectFit="contain"
                priority
                layout="fill"
              />
            </div>
          </div>

          <div className="w-full  md:ml-4 md:w-1/2 md:mt-14 xl:mt-0 mt-[2rem] pb-8 ">
            <h3 className="md:w-9/10 font-bold text-xl leading-normal">{content.subheading}</h3>
            <ul className="text-base xl:text-lg mt-9 md:mt-4 xl:mt-7 w-full md:w-[21.75rem] xl:w-[28rem]">
              {content.bullets.map((bullet, index) => (
                <li key={index} className="mb-2">
                  {bullet}
                </li>
              ))}
            </ul>
            <p className="text-base xl:text-lg mt-4 md:mt-7 xl:mt-9 w-full md:w-[21.75rem] xl:w-[28rem]">
              {content.paragraph}
            </p>
            <div className="flex flex-col md:flex-row md:space-x-4">
              <Link href={routes.register}>
                <a className="min-w-[9rem] flex items-center justify-center md:max-w-fit w-full ml:0 md:w-auto px-4 py-3 text-base font-bold transition duration-300 ease-in-out transform rounded shadow mt-7 xl:mt-12 bg-landing-orange text-landing-blue hover:scale-105">
                  Apply now
                </a>
              </Link>
              {/* <Link href={routes.searchHomes}>
                <a className="min-w-[9rem] flex items-center justify-center md:max-w-fit w-full ml:0 md:w-auto px-4 py-3 text-base font-bold transition duration-300 ease-in-out transform rounded shadow mt-7 xl:mt-12 bg-landing-cherry text-landing-white hover:scale-105">
                  Browse homes
                </a>
              </Link> */}
            </div>
          </div>
        </div>
      </SubLayout>
    </div>
  );
};

export default Neighbourhood;
